import GridCard from '@Guidelines/Component/GridCard';
import { theme } from '@Guidelines/Theme/theme';
import FacilityCardActions from '@UI/Pages/Agencies/Facilities/FacilityCard/FacilityCardActions';
import type FacilityView from '@UI/Store/Facilities/Views/FacilityView';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Tag } from '@verclam/ui-kit';

const FACILITY_CARD_TRACK_COUNT = 4;

const nameStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

/** Row of stacked value/label pairs — one per counter, ready to host more than the user count. */
const countsRowStyle = css`
	display: flex;
	gap: 1.375rem;
`;

const countPairStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
`;

const countValueStyle = css`
	${theme.typography.metric}
	color: ${theme.colors.accent};
`;

const countLabelStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const warningStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.states.warning.ink};
`;

const detailStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

/** Stacks the warning, status tag, address and phone number, one per line. */
const detailsStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

type Props = {
	readonly facility: FacilityView;
};

/**
 * One facility, as an `itemTemplate` cell of the `DataView` grid, laid out as a four-track subgrid:
 * name, user count, a details block, and actions. The details block stacks an optional
 * unassigned-users warning, an optional status tag (never rendered today — no status but `active`
 * exists yet), and an optional address/phone, each omitted when the API sent `null` rather than an
 * empty string.
 */
const FacilityCard: React.FC<Props> = ({ facility }) => {
	return (
		<GridCard trackCount={FACILITY_CARD_TRACK_COUNT}>
			<h2 css={nameStyle}>{facility.name}</h2>
			<div css={countsRowStyle}>
				<div css={countPairStyle}>
					<span css={countValueStyle}>{facility.assignedUserCount}</span>
					<span css={countLabelStyle}>utilisateurs</span>
				</div>
			</div>
			{/* Always rendered, even empty: this is the subgrid's third row track. Skipping it when
			none of the four conditions below hold would drop the track for this card only, shifting its
			actions out of alignment with sibling cards that do have details to show. */}
			<div css={detailsStyle}>
				{facility.hasNoAssignedUser && <p css={warningStyle}>Aucun utilisateur affecté</p>}
				{facility.status.value !== 'active' && (
					<Tag value={facility.status.label} severity="secondary" />
				)}
				{facility.address !== null && <p css={detailStyle}>{facility.address}</p>}
				{facility.phoneNumber !== null && <p css={detailStyle}>{facility.phoneNumber}</p>}
			</div>
			<FacilityCardActions facility={facility} />
		</GridCard>
	);
};

export default FacilityCard;
